import { subscribe } from 'valtio/vanilla'
import {
  highScoreStore,
  getAllHighScores,
  moveInitialLeft,
  moveInitialRight,
  cycleLetterUp,
  cycleLetterDown,
  confirmEntry,
  cancelEntry,
  hideHighScores,
  type HighScoreEntry,
} from '../state/highscore'
import { arcadeStore } from '../state/store'

/**
 * High Score Overlay Component
 * Manages the DOM overlay for high score entry and viewing.
 */

// ============================================================================
// DOM Elements
// ============================================================================

let overlay: HTMLElement | null = null
let overlayModal: HTMLElement | null = null
let entrySection: HTMLElement | null = null
let viewSection: HTMLElement | null = null
let scoreDisplay: HTMLElement | null = null
let initialsSelector: HTMLElement | null = null
let gamesListContainer: HTMLElement | null = null
let keydownHandler: ((e: KeyboardEvent) => void) | null = null
let clickHandler: ((e: MouseEvent) => void) | null = null

// ============================================================================
// Rendering
// ============================================================================

/**
 * Format date for display (MM/DD/YY)
 */
function formatDate(isoDate: string): string {
  try {
    const date = new Date(isoDate)
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const year = String(date.getFullYear()).slice(-2)
    return `${month}/${day}/${year}`
  } catch {
    return '??/??/??'
  }
}

/**
 * Render the initials selector
 */
function renderInitials(): void {
  if (!initialsSelector) return

  const slots = initialsSelector.querySelectorAll('.initial-slot')
  slots.forEach((slot, index) => {
    const letterSpan = slot.querySelector('.initial')
    if (letterSpan) {
      letterSpan.textContent = highScoreStore.currentInitials[index]
    }

    // Update selected state
    if (index === highScoreStore.selectedPosition) {
      slot.classList.add('selected')
    } else {
      slot.classList.remove('selected')
    }
  })
}

/**
 * Render high scores for a single game
 */
function renderGameScores(gameId: string, gameName: string, scores: HighScoreEntry[]): string {
  const rows =
    scores.length > 0
      ? scores
          .map(
            (entry, index) => `
      <div class="highscore-row">
        <span class="highscore-rank">${index + 1}.</span>
        <span class="highscore-name">${entry.name}</span>
        <span class="highscore-score">${entry.score.toLocaleString()}</span>
        <span class="highscore-date">${formatDate(entry.datetime)}</span>
        <span class="highscore-version">v${entry.gameVersion}</span>
      </div>
    `
          )
          .join('')
      : '<p class="highscore-empty">No scores yet</p>'

  return `
    <div class="highscore-game-section" data-game="${gameId}">
      <h3 class="highscore-game-title">${gameName}</h3>
      <div class="highscore-list">
        ${rows}
      </div>
    </div>
  `
}

/**
 * Render the high scores view for all games
 */
function renderHighScoresView(): void {
  if (!gamesListContainer) return

  const allScores = getAllHighScores()

  // Get game names from the arcade store
  const gameNames: Record<string, string> = {}
  arcadeStore.games.forEach((game) => {
    if (!game.id.startsWith('placeholder')) {
      gameNames[game.id] = game.name
    }
  })

  // Render each game's scores
  let html = ''
  for (const gameId of Object.keys(gameNames)) {
    const scores = allScores[gameId] ?? []
    html += renderGameScores(gameId, gameNames[gameId], scores)
  }

  if (html === '') {
    html = '<p class="text-theme-secondary text-sm">No games registered</p>'
  }

  gamesListContainer.innerHTML = html
}

/**
 * Update the overlay visibility and content based on store state
 */
function updateOverlay(): void {
  if (!overlay || !entrySection || !viewSection) return

  const { isEntryActive, isViewingScores, pendingEntry } = highScoreStore

  if (isEntryActive && pendingEntry) {
    // Show entry mode
    overlay.classList.remove('hidden')
    entrySection.classList.remove('hidden')
    viewSection.classList.add('hidden')

    // Update score display
    if (scoreDisplay) {
      scoreDisplay.textContent = `SCORE: ${pendingEntry.score.toLocaleString()}`
    }

    renderInitials()
  } else if (isViewingScores) {
    // Show view mode
    overlay.classList.remove('hidden')
    entrySection.classList.add('hidden')
    viewSection.classList.remove('hidden')

    renderHighScoresView()
  } else {
    // Hide overlay
    overlay.classList.add('hidden')
    entrySection.classList.add('hidden')
    viewSection.classList.add('hidden')
  }
}

// ============================================================================
// Event Handlers
// ============================================================================

/**
 * Handle keyboard input for the overlay
 */
function handleKeyDown(e: KeyboardEvent): void {
  const { isEntryActive, isViewingScores } = highScoreStore

  if (isEntryActive) {
    switch (e.key) {
      case 'ArrowLeft':
      case 'a':
        moveInitialLeft()
        e.preventDefault()
        e.stopPropagation()
        break
      case 'ArrowRight':
      case 'd':
        moveInitialRight()
        e.preventDefault()
        e.stopPropagation()
        break
      case 'ArrowUp':
      case 'w':
        cycleLetterUp()
        e.preventDefault()
        e.stopPropagation()
        break
      case 'ArrowDown':
      case 's':
        cycleLetterDown()
        e.preventDefault()
        e.stopPropagation()
        break
      case 'Enter':
        confirmEntry()
        e.preventDefault()
        e.stopPropagation()
        break
      case 'Escape':
        cancelEntry()
        e.preventDefault()
        e.stopPropagation()
        break
    }
  } else if (isViewingScores) {
    // Any key closes the view
    hideHighScores()
    e.preventDefault()
    e.stopPropagation()
  }
}

/**
 * Handle global click to close overlay when clicking outside modal
 */
function handleOverlayClick(e: MouseEvent): void {
  const { isEntryActive, isViewingScores } = highScoreStore

  // Only process if overlay is active
  if (!isEntryActive && !isViewingScores) {
    return
  }

  // Only close if clicking outside the modal content
  if (overlayModal && !overlayModal.contains(e.target as Node)) {
    // Prevent the click from reaching underlying UI elements
    e.preventDefault()
    e.stopPropagation()

    if (isEntryActive) {
      cancelEntry()
    } else if (isViewingScores) {
      hideHighScores()
    }
  }
}

/**
 * Setup click handlers for the initial slots
 */
function setupInitialSlotHandlers(): void {
  if (!initialsSelector) return

  const slots = initialsSelector.querySelectorAll('.initial-slot')
  slots.forEach((slot, index) => {
    // Click on slot to select it
    slot.addEventListener('click', () => {
      highScoreStore.selectedPosition = index
    })

    // Up button
    const upBtn = slot.querySelector('.initial-up')
    if (upBtn) {
      upBtn.addEventListener('click', (e) => {
        e.stopPropagation()
        highScoreStore.selectedPosition = index
        cycleLetterUp()
      })
    }

    // Down button
    const downBtn = slot.querySelector('.initial-down')
    if (downBtn) {
      downBtn.addEventListener('click', (e) => {
        e.stopPropagation()
        highScoreStore.selectedPosition = index
        cycleLetterDown()
      })
    }
  })
}

// ============================================================================
// Initialization
// ============================================================================

/**
 * Initialize the high score overlay
 */
export function initHighScoreOverlay(): void {
  // Get DOM elements
  overlay = document.getElementById('highscore-overlay')
  overlayModal = overlay?.querySelector('.highscore-modal') ?? null
  entrySection = document.getElementById('highscore-entry')
  viewSection = document.getElementById('highscore-view')
  scoreDisplay = document.getElementById('entry-score')
  initialsSelector = document.getElementById('initials-selector')
  gamesListContainer = document.getElementById('highscore-games-list')

  if (!overlay) {
    console.error('High score overlay element not found')
    return
  }

  // Setup initial slot click handlers
  setupInitialSlotHandlers()

  // Setup keyboard handler (capture phase to intercept before game)
  keydownHandler = handleKeyDown
  document.addEventListener('keydown', keydownHandler, true)

  // Setup global click-outside handler
  clickHandler = handleOverlayClick
  document.addEventListener('click', clickHandler, true)

  // Subscribe to store changes
  subscribe(highScoreStore, updateOverlay)

  // Initial render
  updateOverlay()
}

/**
 * Cleanup the high score overlay (for testing)
 */
export function cleanupHighScoreOverlay(): void {
  if (keydownHandler) {
    document.removeEventListener('keydown', keydownHandler, true)
    keydownHandler = null
  }
  if (clickHandler) {
    document.removeEventListener('click', clickHandler, true)
    clickHandler = null
  }
}
